import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import format from 'date-fns/format';
import MiniUserDetail from '../../components/MiniUserDetail/MiniUserDetail';
import useCookies from 'react-cookie/es6/useCookies';
import { v4 as uuidv4 } from 'uuid';
import NoVideo from './NoVideo';
import useMount from '../../../common/hooks/useMount';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import { Icon } from '../../components/Icon';
import SharingModal from '../../components/_stateful/SharingModal';
import SharingModalContent from '../../components/_stateful/SharingModal/SharingModalContent';
import { VideoHeader } from './VideoHeader/VideoHeader';
import Toast from '../../../common/utils/Toast';
import { useUIContext } from '../../context/UIContext';
import VideoService from '../../service/VideoService';
import styles from './Video.module.scss';
import { useUserContext } from '../../context/UserContext';
import ResourceHelper from '../../utils/ResourceHelper';
import { CustomButton } from '../../components/Button/Button';
import cx from 'classnames';

import MobileHeader from '../../components/MobileHeader';
import DotsBtn from '../../components/Menu/DotsBtn';
import useOutsideClick from '../../hooks/useOutsideClick';
import Comments from '../../components/Comments';
import { useTranslation } from 'react-i18next';
import { detectMob } from '../../utils/isMobile';
import DownloadModal from '../../components/_stateful/DownloadModal';
import DownloadModalContent from '../../components/_stateful/DownloadModal/DownloadModalContent';
import { useHistory, useLocation } from 'react-router-dom';
import { parseString } from '../../utils/stringHelper';
import AnalyticsInfo from '../../components/AnalyticsInfo/AnalyticsInfo';
import { ToastNotification } from '../../components/Toast/Toast';
import CmsAPI from '../../../common/apis/CmsAPI';
import Modal from '../../components/Modal';
import Reactions from '../../components/Comments/Reactions/Reactions';
import ReactionPicker from '../../components/Comments/CommentActions/ReactionPicker';
import NameField from '../../components/Comments/CommentActions/NameField';
import Hint from '../../components/Hint/Hint';
import { withProtectionCheck } from '../../hocs/withProtectionCheck';
import CardMenu from '../../components/CardMenu/CardMenu';
import { useCallToActionContext } from '../../context/CallToActionContext';
import Content from '../../components/Content/Content';
import { getCurrentActions } from '../../utils/CallToActionHelper';
import PlayedVideoCallToAction from '../../features/CallToAction/components/PlayedVideoCallToAction/PlayedVideoCallToAction';
import { useChaptersContext } from '../../context/ChaptersContext';
import { STORY_USER_RELATION } from '../../constants/UserRoles';
import TagsList from '../CompanySettings/components/Tags/TagsList/TagsList';
import VideoDetailsPlayer from '../../features/Dashboard/components/VideoDetailsPlayer/VideoDetailsPlayer';
import { useInPlayerCommentContext } from '../../context/InPlayerCommentContext';

const videoInit = {
  id: '',
  video: '',
  thumbnail: '',
  author: null,
};

const shareModalInit = {
  open: false,
  id: '',
  thumbnail: '',
  url: '',
  trackerURL: '',
};

const downloadModalInit = {
  open: false,
};

const ID_COOKIE = 'engage-viewer-id';
const cookieOptions = {
  secure: true,
  sameSite: 'strict',
  expires: new Date('Fri, 31 Dec 2099 23:59:59'),
};

const SharedVideo = props => {
  const { videoId, isProtected, videoPassword, videoDetails, role, permissions, initialVideoConfig: videoConfig } = props;

  const isUserHasEditingRights = useMemo(
    () =>
      role === STORY_USER_RELATION.OWNER.TYPE ||
      role === STORY_USER_RELATION.FOLDER_OWNER.TYPE ||
      role === STORY_USER_RELATION.FOLDER_EDITOR.TYPE ||
      role === STORY_USER_RELATION.FOLDER_ADVANCED_EDITOR.TYPE ||
      role === STORY_USER_RELATION.FOLDER_VIEWER.TYPE,
    [role]
  );

  const { user, reloadUser, logFirebaseEvent, notLoggedIn, initializeFirebase, isViewAnalyticsEnabled, userRole } = useUserContext();

  const { miniSidebar, isMobileView } = useUIContext();

  const [initialCheckLoading, setInitialCheckLoading] = useState(true);
  const [isFirstPlayed, setIsFirstPlayed] = useState(false);

  const [video, setVideo] = useState(videoInit);
  const [noVideo, setNoVideo] = useState(false);
  const [cookies, setCookie] = useCookies(['engage-viewer-id']);
  const [shareModal, setShareModal] = useState(shareModalInit);
  const [downloadModal, setDownloadModal] = useState(downloadModalInit);
  const [reply, setReply] = useState({ show: false, timestamp: false });
  const [canComment, setCanComment] = useState(false);
  const [canViewAnalytics, setCanViewAnalytics] = useState(true);
  const [canDownload, setCanDownload] = useState(true);
  const [canShare, setCanShare] = useState(true);
  const [playerOnly, setPlayerOnly] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [selectedReactions, setSelectedReactions] = useState('');

  const cachedName = useMemo(() => localStorage.getItem('commenterName'), []);

  const [currentUserName, setCurrentUserName] = useState(null);
  const [showNameInputField, setShowNameInputField] = useState(false);
  const [cachedReaction, setCachedReaction] = useState({});
  const [localStorageStoryReactions, setLocalStorageStoryReactions] = useState(JSON.parse(localStorage.getItem(videoId)));
  const [reactionStatistics, setReactionStatistics] = useState([]);
  const [reactionStatisticsBar, setReactionStatisticsBar] = useState([]);
  const [showDownloadMessage, setShowDownloadMessage] = useState(false);

  const [analyticsInfo, setAnalyticsInfo] = useState([]);
  const [showAnalyticsInfo, setShowAnalyticsInfo] = useState(false);
  const [commentsWithTimestamp, setCommentsWithTimestamp] = useState([]);

  const companyConfig = useMemo(() => video?.appConfig, [video]);
  const [latestExternalUpload, setLatestExternalUpload] = useState(null);

  const { chapters, updateInitialChapters } = useChaptersContext();

  const endCtaComponent = useMemo(() => (user ? null : <PlayedVideoCallToAction storyId={videoId} />), [user, videoId]);

  const descriptionRef = useRef(null);

  const isVideoAuthor = useMemo(() => user && user?.username === video?.author?.username, [user, video]);
  const isAnalyticsNotEmpty = useMemo(() => analyticsInfo?.cta?.length || analyticsInfo?.views?.length, [analyticsInfo]);

  const isStoryCollaborator = useMemo(() => {
    if (role !== STORY_USER_RELATION.VIEWER.TYPE) {
      return true;
    }
    return role !== STORY_USER_RELATION.VIEWER.TYPE || (permissions?.allowedScenes?.length || permissions?.readStory);
  }, [role, permissions]);

  const { startAddNotesMode } = useInPlayerCommentContext();

  useEffect(() => {
    if (descriptionRef.current && video?.description) {
      const getDescriptionElements = description => {
        const splittedBySpace = description
          ?.split('\n')
          .map(item => item.split(' '))
          .map(space =>
            space
              .map(item => {
                if (
                  item.match(/^(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)$/g)
                ) {
                  const addProtocol = !item.match(/^(http(s)?:\/\/.)/g);
                  return `<a href=${addProtocol ? `https://${item}` : item} target="_blank">${item}</a>`;
                } else {
                  return item;
                }
              })
              .join(' ')
          )
          .join('\n');

        return splittedBySpace;
      };

      descriptionRef.current.innerHTML = getDescriptionElements(video.description);
    }
  }, [descriptionRef, video]);

  const history = useHistory();
  const location = useLocation();

  const { updateCallActions, updateInitialCallActions, resetCallActions, callActions } = useCallToActionContext();

  const { t } = useTranslation();

  const downloadNotification = useMemo(() => {
    if (video?.isProtected) {
      return t('my_video.download.protected_notification');
    }

    return t('my_video.download.not_supported_notification');
  }, [video]);

  const menuRef = useRef(null);

  useOutsideClick(menuRef, () => {
    if (showMenu) {
      setShowMenu(false);
    }
  });

  useEffect(() => {
    loadDetails(videoDetails);
  }, []);

  const updateVideoData = (videoDetails, videoPath) => {
    videoDetails.description = parseString(videoDetails.description);
    videoDetails.name = parseString(videoDetails.name);

    const createDate = format(new Date(Date.parse(`${videoDetails.createDate}`)), 'dd MMM yyyy');
    const updateDate = format(new Date(Date.parse(`${videoDetails.updateDate}`)), 'dd MMM yyyy');

    setVideo({ ...videoDetails, videoPath, createDate, updateDate });
  };

  const getSettingValueByName = (settings, name, defaultValue = true) => {
    return (settings != null && name in settings) ? settings[name] : defaultValue;
  }

  const loadDetails = async (initDetails = null) => {
    try {
      let videoDetails;
      if (initDetails === null) {
        if (isProtected) {
          videoDetails = await VideoService.getProtectedVideo(videoId, videoPassword);
        } else {
          videoDetails = await VideoService.getVideoDetails(videoId);
        }
      } else {
        videoDetails = { ...initDetails };
      }

      // If story unfinished - redirect to 404
      if (videoDetails.video === null) {
        history.push('/eg/404');
      }

      // Apply other video preferences
      const { settings = null } = videoDetails;
      setCanComment(getSettingValueByName(settings, 'canComment', false));
      setCanDownload(getSettingValueByName(settings, 'canDownload'));
      setCanViewAnalytics(getSettingValueByName(settings, 'canViewAnalytics'));
      setCanShare(getSettingValueByName(settings, 'canShare'));
      setPlayerOnly(
        location.search.includes('embedded')
        || getSettingValueByName(settings, 'sharePlayerOnly', false)
      );
      setShowSidebar(getSettingValueByName(settings, 'showSidebar'));
      setInitialCheckLoading(false);

      const hlsVideo = ResourceHelper.getResourceByLabel(videoDetails, 'hls')?.url;

      let videoPath = hlsVideo ? `${hlsVideo}?${Date.now()}` : videoDetails.video;

      const externalUploaded = ResourceHelper.getAllResourcesByType(videoDetails, 'externalUpload');
      if (externalUploaded?.length) {
        const latestExternalVersion = externalUploaded.pop();
        videoPath = latestExternalVersion?.url;
        setLatestExternalUpload(latestExternalVersion);
      }

      setReactions(videoDetails?.reactions ?? []);

      updateVideoData(videoDetails, videoPath, hlsVideo);

      if (videoDetails?.video == null) {
        setNoVideo(true);
      }
    } catch (err) {
      if (err) {
        if (isProtected === false) {
          window.location = '/eg/404';
        }
        console.log('errorMessage', err);
      }
    }
  };

  useMount(() => {
    if (cookies[ID_COOKIE]) {
      // TODO: validate uuid
      return;
    }
    setCookie(ID_COOKIE, uuidv4(), cookieOptions);
  });

  const shareVideo = useCallback(async id => {
    try {
      setShareModal({
        open: true,
        id: id,
      });
    } catch (e) {
      Toast.error(e);
    }
  }, []);



  const handleCopyClick = async id => {
    if (!document.hasFocus()) {
      document.focus();
    }
    try {
      await navigator.clipboard.writeText(VideoService.getShareURL(id));
      logFirebaseEvent('copy_link_cl', { story_id: id });
      ToastNotification({
        message: t('general.toast.link_copied'),
        iconName: 'success',
        isMobileView: isMobileView,
      });
    } catch (e) {
      console.log('Error copying link', e);
    }
  };

  const handleMouseOver = useCallback(() => {
    video?.isProtected && setShowDownloadMessage(true);
  }, [video]);

  const handleMouseLeave = useCallback(() => {
    video?.isProtected && setShowDownloadMessage(false);
  }, [video]);

  const handleReaction = reaction => {
    if (!currentUserName) {
      // If not logged in and LocalStorage username is null
      setCachedReaction(reaction);
      setShowNameInputField(true);
    } else {
      if (selectedReactions?.some(item => item.unicode === reaction.unicode) && user) {
        // Delete if reaction exist, otherwise add new one
        handleDelete(reaction);
      } else if (user || (!user && !localStorageStoryReactions?.some(unicode => unicode === reaction.unicode))) {
        addReaction(reaction);
      }
    }
  };

  const handleDelete = obj => {
    reactions?.forEach(reaction => {
      if (reaction.username === user?.username && obj.unicode === reaction.unicode) {
        setReactions(reactions => reactions.filter(item => item.id !== reaction.id));
        setSelectedReactions(reactions => reactions.filter(item => item.id !== reaction.id));
        deleteReaction(reaction);
      }
    });
  };

  const deleteReaction = async reaction => {
    await CmsAPI.deleteReaction(reaction.id).catch(e => {
      console.log(e);
    });
  };

  const addReaction = async reaction => {
    await CmsAPI.insertReaction({
      storyId: videoId,
      username: user ? user.username : null,
      authorName: user ? user.userFullName : currentUserName,
      unicode: reaction.unicode,
    })
      .then(response => updateReactions(response))
      .catch(e => {
        console.log(e);
      });
  };

  const updateReactions = response => {
    setReactions(prevState => [...prevState, response.result]);
    console.log('localStorageStoryReactions', localStorageStoryReactions);
    if (localStorageStoryReactions) {
      setLocalStorageStoryReactions(prevState => [...prevState, response.result.unicode]);
    } else {
      console.log('localStorageStoryReactions null', response.result.unicode);
      setLocalStorageStoryReactions([response.result.unicode]);
    }
  };

  const createStatistics = () => {
    let arr = [];
    reactions?.map(reaction => {
      let obj = { unicode: '', amount: 0, selected: false };
      if (!arr.some(e => e.unicode === reaction.unicode)) {
        // Check if the emoji already exists in the array
        const isSelected = selectedReactions?.some(el => el.unicode === reaction.unicode); // Check if Emoji is in Selected Array
        obj.unicode = reaction.unicode;
        obj.amount = reactions.filter(function (item) {
          return item.unicode === reaction.unicode;
        }).length;
        obj.selected = isSelected;
        if (reactionStatisticsBar.length > 3 && isSelected && !reactionStatisticsBar?.some(el => el.unicode === reaction.unicode)) {
          arr.unshift(obj);
        } else {
          arr.push(obj);
        }
      }
    });
    setReactionStatistics(arr);
    setReactionStatisticsBar(arr.slice(0, 4));
  };

  // Download function for player only mode
  const downloadVideo = useCallback(() => {
    const videoElement = document.querySelector('video');
    if (videoElement) {
      !videoElement.paused && videoElement.pause();
    }

    if (latestExternalUpload == null) {
      setDownloadModal({ open: true });
    } else {
      fetch(latestExternalUpload.url)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch video');
          }
          return response.blob(); // Get the file as a Blob
        })
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `${video.name}_${latestExternalUpload.properties.name}.mp4`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url); // Clean up the Blob URL
        })
        .catch(error => {
          console.error('Error downloading video:', error);
          ToastNotification({
            message: t('download_modal.fail'),
          });
        });
    }

  }, [latestExternalUpload, video]);

  useEffect(() => {
    const initUser = async () => {
      reloadUser();
    };
    if (!user) initUser();

    if (user && user.userFullName) {
      setCurrentUserName(user.userFullName);
    } else if (cachedName !== null) {
      setCurrentUserName(cachedName);
    }
  }, [user]);

  useEffect(() => {
    setLocalStorageStoryReactions(JSON.parse(localStorage.getItem(video.id)));
  }, [video]);

  useEffect(() => {
    if (playerOnly) {
      const delay = 500;
      const timeoutId = setTimeout(() => {
        window.$crisp.push(['do', 'session:reset']);
        window.$crisp.push(['do', 'chat:close']);
        window.$crisp.push(['do', 'chat:hide']);
      }, delay);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [playerOnly]);

  useEffect(() => {
    // Set users selected reactions
    let arr = [];
    reactions?.forEach(reaction => {
      if (
        reaction.username === user?.username ||
        (!user && reaction.authorName === currentUserName && localStorageStoryReactions?.some(unicode => unicode === reaction.unicode))
      ) {
        arr.push(reaction);
      }
    });
    console.log('selected reactions', arr, reactions, localStorageStoryReactions);
    setSelectedReactions(arr);
  }, [reactions, localStorageStoryReactions, user]);

  useEffect(() => {
    if (showNameInputField) {
      setShowNameInputField(false);
      handleReaction(cachedReaction);
    }
  }, [currentUserName]);

  useEffect(() => {
    const getVideoAnalytics = async () => {
      try {
        const result = await VideoService.getStoryAnalytics(videoId);
        setAnalyticsInfo(result);
      } catch (err) {
        console.log(err);
      }
    };

    isVideoAuthor && getVideoAnalytics();
  }, [isVideoAuthor]);

  useEffect(() => {
    if (localStorageStoryReactions) {
      localStorage.setItem(video.id, JSON.stringify(localStorageStoryReactions));
    }
  }, [localStorageStoryReactions]);

  useEffect(() => {
    createStatistics();
  }, [selectedReactions]);

  useEffect(() => {
    const retrieveChapters = async () => {
      try {
        const response = await VideoService.getStoryChapters(videoId);
        updateInitialChapters(response.chapters);
      } catch (e) {
        console.log('Error while requesting chapters', e);
      }
    };

    video.videoPath && retrieveChapters();

    return () => resetCallActions();
  }, [video, latestExternalUpload]);

  useEffect(() => {
    const getCallActions = async () => {
      if (videoConfig?.ctas?.length) {
        const currentActions = getCurrentActions(videoConfig?.ctas, video.duration);
        updateCallActions(currentActions);
        updateInitialCallActions(currentActions);
      }
    };

    if (video.duration && !latestExternalUpload) {
      getCallActions();
    }
  }, [video.duration, videoConfig]);

  const handleOpenAnalyticsInfoModal = () => {
    setShowAnalyticsInfo(true);
  };

  const handleCloseAnalyticsInfoModal = () => {
    setShowAnalyticsInfo(false);
  };

  useEffect(() => {
    if (isMobileView) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [isMobileView]);

  useEffect(() => {
    if (notLoggedIn) {
      initializeFirebase();
    }
  }, [notLoggedIn])

  if (initialCheckLoading) {
    return <></>;
  }

  return (
    <>
      {isMobileView && detectMob() && !playerOnly && (
        <MobileHeader
          showMenuBtn={showSidebar}
          userAvatar={user?.avatarUri}
          userFirstLetter={user?.userFullName.charAt(0).toUpperCase()}
        />
      )}
      {playerOnly ? (
        <div className={styles.videoContentPlayerOnly}>
          {noVideo ? (
            <NoVideo />
          ) : (
            <>
              <VideoDetailsPlayer
                isSharedPage={true}
                video={video}
                src={video.videoPath}
                miniRightSidebar={miniSidebar}
                isExternallyEdited={latestExternalUpload !== null}
                isMobileView={isMobileView}
                setIsFirstPlayed={setIsFirstPlayed}
                uuid={video.id}
                cookieID={cookies[ID_COOKIE]}
                overlay
                player={styles.playerOnlyPlayer}
                moduleClassName={styles.playerOnly}
                onDownloadVideo={canDownload ? downloadVideo : undefined}
                playerOnly={true}
                disableDownload={video?.isProtected}
                downloadNotification={downloadNotification}
                isEmbeddedMode={location.search.includes('embedded')}
                companyConfig={companyConfig}
                onEndComponent={endCtaComponent}
                commentsWithTimestamp={commentsWithTimestamp}
                videoConfig={videoConfig}
              />
              {canDownload && !isFirstPlayed ? (
                <CustomButton
                  onMouseOver={handleMouseOver}
                  onMouseLeave={handleMouseLeave}
                  disabled={video?.isProtected}
                  className={styles.downloadBtn}
                  variant="link"
                  onClick={downloadVideo}
                  ariaLabel={t('general.video_action_button.download')}
                >
                  <Icon src="download" className={styles.downloadIcon} />
                </CustomButton>
              ) : null}
              {showDownloadMessage ? <Hint className={styles.notification} text={downloadNotification} /> : null}
            </>
          )}
        </div>
      ) : (
        <>
          {showSidebar ? <ProtectedPageSidebar /> : null}
          <Content
            className={cx(styles.videoContainer, {
              [styles.miniSidebar]: showSidebar && miniSidebar,
              [styles.noSidebar]: !showSidebar,
              [styles.mobile]: isMobileView,
            })}
          >
            <div className={styles.videoContent}>
              {!isMobileView && (
                <VideoHeader
                  sharedMode={true}
                  shareVideo={shareVideo}
                  handleCopyClick={handleCopyClick}
                  video={video}
                  downloadVideo={downloadVideo}
                  canDownload={canDownload}
                  canViewAnalytics={canViewAnalytics}
                  canShare={canShare}
                  analyticsInfo={analyticsInfo}
                  downloadNotification={downloadNotification}
                  isVideoAuthor={isVideoAuthor}
                  isExternallyEdited={latestExternalUpload !== null}
                />
              )}

              {noVideo ? (
                <NoVideo />
              ) : (
                <VideoDetailsPlayer
                  isSharedPage={true}
                  video={video}
                  src={video.videoPath}
                  miniRightSidebar={miniSidebar}
                  isMobileView={isMobileView}
                  isExternallyEdited={latestExternalUpload !== null}
                  uuid={video.id}
                  cookieID={cookies[ID_COOKIE]}
                  disableDownload={video?.isProtected}
                  companyConfig={companyConfig}
                  onEndComponent={endCtaComponent}
                  commentsWithTimestamp={commentsWithTimestamp}
                  videoConfig={videoConfig}
                />
              )}
              <div>
                <div className={styles.dataWrapper}>
                  {isMobileView && (
                    <div className={styles.titleContainer}>
                      <h1 className={styles.title}>{video.video && video.name}</h1>

                      <div className={styles.mobileDotsMenu}>
                        {canShare || canDownload ? <DotsBtn tagName="BUTTON" onClick={() => setShowMenu(true)} /> : null}
                        {showMenu ? (
                          <CardMenu
                            menuRef={menuRef}
                            listMode={false}
                            item={video}
                            itemIndex={0}
                            toggleShowMenu={setShowMenu}
                            handleCopyClick={canShare && handleCopyClick}
                            downloadItem={{
                              action: canDownload && downloadVideo,
                              disabled: video?.isProtected,
                              message: downloadNotification,
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  )}
                  <div className={styles.userInfoWrapper}>
                    <div className={styles.profile}>
                      <MiniUserDetail
                        avatar={video.author?.avatar}
                        size="medium"
                        name={parseString(video.author?.name)}
                        classNames={{ titleClassName: styles.userName, avatarClassName: styles.avatar }}
                      />
                    </div>
                    {!isMobileView && (
                      <div className={styles.postActions}>
                        <div className={styles.postInfo}>
                          <span>{`${t('my_video.last_update')} ${t('general.date', {
                            date: new Date(`${video.updateDate}`),
                          })}`}</span>
                        </div>

                        <div className={styles.commentActionContainer}>
                          <Reactions
                            user={user}
                            reactions={reactions}
                            handleReaction={handleReaction}
                            selectedReactions={selectedReactions}
                            reactionStatisticsBar={reactionStatisticsBar}
                            reactionStatistics={reactionStatistics}
                          />
                          {canComment && (
                            <CustomButton variant="secondary" className={styles.commentButton} onClick={() => setReply({ show: true, timestamp: true })}>
                              <Icon src="comment" verticalAlign="middle" className={cx(styles.commentIcon)} />
                              {t('general.comment')}
                            </CustomButton>
                          )}
                          {canComment && isStoryCollaborator && (
                            <CustomButton variant="secondary" className={styles.commentButton} onClick={startAddNotesMode}>
                              <Icon src="addNote" verticalAlign="middle" className={cx(styles.commentIcon)} />
                              <span>{t('my_video.add_note')}</span>
                            </CustomButton>
                          )}
                          <ReactionPicker handleReaction={handleReaction} isMobileView={isMobileView} isVideoReaction={true} />
                        </div>
                      </div>
                    )}
                    {isMobileView && canShare ? (
                      <CustomButton variant="link" onClick={() => handleCopyClick(video.id)} className={styles.copyLinkMobileBtn} ariaLabel={t('general.video_action_button.link_copied')}>
                        <Icon src="mobile_link" verticalAlign="middle" className={styles.sourceIcon} />
                      </CustomButton>
                    ) : null}
                  </div>
                  {isMobileView && (
                    <div>
                      <div className={styles.commentActionContainer}>
                        {!!reactionStatisticsBar?.length && (
                          <Reactions
                            user={user}
                            reactions={reactions}
                            handleReaction={handleReaction}
                            selectedReactions={selectedReactions}
                            reactionStatisticsBar={reactionStatisticsBar}
                            reactionStatistics={reactionStatistics}
                            isMobileView={isMobileView}
                          />
                        )}
                        <div className={styles.reactionsCommentContainer}>
                          {canComment && (
                            <CustomButton variant="secondary" className={styles.commentButton} onClick={() => setReply({ show: true, timestamp: true })}>
                              <Icon src="comment" verticalAlign="middle" className={cx(styles.commentIcon)} />
                              {t('general.comment')}
                            </CustomButton>
                          )}
                          <ReactionPicker handleReaction={handleReaction} isMobileView={isMobileView} isVideoReaction={true} />
                        </div>
                      </div>
                      <div className={styles.mobileInfoContainer}>
                        <p>{t('general.date', { date: new Date(`${video.createDate}`) })}</p>
                        {canViewAnalytics ? (
                          <>
                            {isVideoAuthor && isAnalyticsNotEmpty && isViewAnalyticsEnabled ? (
                              <div className={styles.viewsContainer} onClick={handleOpenAnalyticsInfoModal}>
                                <span className={styles.viewsText}>{t('analytics.title')}</span>
                              </div>
                            ) : (
                              <div className={styles.viewsContainer}>
                                <span className={styles.viewsText}>
                                  {video?.stats?.views || 0} {video?.stats?.views === 1 ? t('general.view') : t('general.views')}
                                </span>
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}
                  <div className={styles.descriptionContainer} ref={descriptionRef} />
                  {isUserHasEditingRights && (
                    <ul className={styles.sharedTagsList}>
                      <TagsList tags={video.tags} />
                    </ul>
                  )}
                  {canComment ? (
                    <div className={styles.replyDescription}>
                      <>
                        <div className={styles.commentContainer} id="newComment">
                          <p className={styles.commentTitle}>{t('my_video.general.comments')}</p>
                        </div>
                        {showNameInputField && (
                          <NameField
                            setUsername={setCurrentUserName}
                            cachedReaction={cachedReaction}
                            setShowNameInputField={setShowNameInputField}
                          />
                        )}
                      </>
                    </div>
                    ) : null}
                    <Comments
                      storyId={video.id}
                      user={user}
                      currentUserName={currentUserName}
                      setCurrentUserName={setCurrentUserName}
                      showNewCommentForm={reply}
                      setShowNewCommentForm={setReply}
                      author={video.author}
                      chapters={chapters}
                      callActions={callActions}
                      isSharedPage={true}
                      ignoreTimestampsBefore={latestExternalUpload?.created}
                      disableInPlayerComments={!isStoryCollaborator}
                      canComment={canComment}
                    />
                </div>
              </div>
            </div>
            <SharingModal open={shareModal.open} onClose={() => setShareModal(shareModalInit)}>
              <div className={styles.contentContainer}>
                {shareModal.open && (
                  <SharingModalContent
                    onClose={() => setShareModal(shareModalInit)}
                    videoId={shareModal.id}
                    videoPassword={videoPassword}
                    isSkipAuthToken
                  />
                )}
              </div>
            </SharingModal>
            {isMobileView && (
              <Modal className={styles.viewsContainerModal} open={showAnalyticsInfo} onClose={handleCloseAnalyticsInfoModal}>
                <AnalyticsInfo info={analyticsInfo} onClose={handleCloseAnalyticsInfoModal} isMobileView={isMobileView} />
              </Modal>
            )}
          </Content>
        </>
      )}
      <DownloadModal open={downloadModal.open} onClose={() => setDownloadModal(downloadModalInit)}>
        <div className={styles.contentContainer}>
          <DownloadModalContent onClose={() => setDownloadModal(downloadModalInit)} video={video} setVideo={setVideo} user={user} />
        </div>
      </DownloadModal>
    </>
  );
};

export default withProtectionCheck(SharedVideo);
